"use client";

import React, { useEffect, useRef } from "react";
import Link from "next/link";
import {
  projects,
  ProjectProps,
} from "../../components/work/projectDetails.ts";
import ProjectDetailsLayout from "../../components/work/ProjectDetailsLayout.tsx";
import Container from "../../components/container/Container.tsx";
import AnimatedTitle from "../../animations/AnimatedTitle.tsx";
import ImageSlider from "../../components/work/ImageSlider.tsx";
import ImageGallery from "../../components/work/ImageGallery.tsx";
import { SiGithub } from "react-icons/si";
import { notFound, usePathname } from "next/navigation";
import { RiArrowRightLine } from "react-icons/ri";
import PageTransition from "../../components/other/PageTransition";
import { motion } from "framer-motion";

export default function ProjectPage({
  params,
}: {
  params: Promise<{ id: string }>;
}) {
  const { id } = React.use(params);
  const pathname = usePathname();
  const contentRef = useRef<HTMLDivElement>(null);

  const currentId = parseInt(id);
  const project = projects.find((p: ProjectProps) => p.id === currentId);

  // Scroll to top when pathname changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  if (!project) {
    notFound();
    return null;
  }

  // Find next project, loop back to first if at end
  const nextProject = projects.find((p) => p.id > currentId) || projects[0];

  type IconType = React.ComponentType<{ className?: string }>;
  const bulletPattern = /^(?:â€¢|•|-|\*)\s*/;

  return (
    <PageTransition>
      <ProjectDetailsLayout project={project}>
        <div className="relative mx-auto flex w-full max-w-[1600px] flex-col">
          {/* Navigation */}
          <div className="mb-6 flex justify-between gap-4">
            <Link
              href="/history/v2"
              className="flex items-center gap-2 rounded-full bg-white/10 px-6 py-2 text-white transition-all duration-300 hover:translate-x-[-4px] hover:bg-white/20"
              data-blobity
              data-blobity-radius="24"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-5 w-5"
              >
                <path
                  fillRule="evenodd"
                  d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                />
              </svg>
              Back
            </Link>
            <div className="flex gap-4">
              {project.demo && (
                <Link
                  href={project.demo}
                  target="_blank"
                  className="flex items-center gap-2 rounded-full bg-white px-6 py-2 text-black transition-all duration-300 hover:scale-105 hover:bg-white/90"
                  data-blobity
                  data-blobity-radius="24"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-5 w-5"
                  >
                    <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z" />
                    <path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z" />
                  </svg>
                  Visit Site
                </Link>
              )}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  href={`/history/v2/projects/${nextProject.id}`}
                  className="group flex items-center gap-2 rounded-full bg-white/10 px-6 py-2 text-white transition-all duration-300 hover:translate-x-[4px] hover:bg-white/20"
                  data-blobity
                  data-blobity-radius="24"
                >
                  Next Project
                  <RiArrowRightLine className="h-5 w-5 transform transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-col">
            {/* Hero Section */}
            <div className="mb-12 px-4 md:px-8">
              <div className="project-page-container relative w-full">
                <Container
                  width="100%"
                  height="auto"
                  borderRadius={25}
                  color="rgba(255, 255, 255, 0.1)"
                  blur={false}
                  grain={true}
                  top="0px"
                  left="0px"
                  angle={0}
                >
                  <div
                    ref={contentRef}
                    className="relative z-10 flex flex-col items-start gap-12 p-8 lg:flex-row lg:p-12"
                  >
                    <div className="flex flex-col pb-12 lg:w-[45%]">
                      <AnimatedTitle
                        text={project.name}
                        className="mb-6 text-[40px] leading-none md:text-[44px] lg:text-[48px]"
                        wordSpace="mr-[0.25em]"
                        charSpace="-mr-[0.01em]"
                      />

                      {/* Action Buttons - Moved to upper section */}
                      <div className="mb-8 flex gap-4">
                        {project.available && project.github && (
                          <Link
                            href={project.github}
                            target="_blank"
                            className="flex items-center gap-2 rounded-full bg-white/10 px-6 py-2 text-white transition-colors hover:bg-white/20"
                            data-blobity
                            data-blobity-radius="24"
                          >
                            <SiGithub className="text-xl" />
                            View Code
                          </Link>
                        )}
                        {project.id === 2 && (
                          <Link
                            href="/documate"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-full bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-500"
                            data-blobity
                            data-blobity-radius="24"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="h-5 w-5"
                            >
                              <path d="M12 1.5a.75.75 0 01.75.75V7.5h-1.5V2.25A.75.75 0 0112 1.5zM11.25 7.5v5.69l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V7.5h3.75a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9a3 3 0 013-3h3.75z" />
                            </svg>
                            Download App
                          </Link>
                        )}
                      </div>

                      <div className="space-y-4 text-[16px] font-normal leading-relaxed text-[#95979D]">
                        {project.pageDescription
                          ?.split("\n")
                          .map((paragraph, idx) => {
                            if (!paragraph.trim()) return null;

                            // Check if this is a bullet point
                            if (bulletPattern.test(paragraph.trim())) {
                              return (
                                <div
                                  key={idx}
                                  className="mb-1 flex items-start gap-3"
                                >
                                  <span className="mt-1 flex-shrink-0 text-white">
                                    •
                                  </span>
                                  <span className="flex-1">
                                    {paragraph.trim().replace(bulletPattern, "")}
                                  </span>
                                </div>
                              );
                            }

                            // Check if this is a heading-like text (ends with colon or is all caps)
                            if (
                              paragraph.trim().endsWith(":") ||
                              (paragraph.trim() ===
                                paragraph.trim().toUpperCase() &&
                                paragraph.trim().length > 5)
                            ) {
                              return (
                                <h3
                                  key={idx}
                                  className="mt-6 mb-2 text-lg font-bold text-white first:mt-0"
                                >
                                  {paragraph.trim()}
                                </h3>
                              );
                            }

                            // Regular paragraph
                            return (
                              <p key={idx} className="leading-relaxed">
                                {paragraph.trim()}
                              </p>
                            );
                          })}
                      </div>

                      <div className="mt-10">
                        <h3 className="mb-4 text-xl font-semibold">
                          Technologies Used
                        </h3>
                        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                          {project.technologies.map(
                            (IconComponent: IconType, idx: number) => (
                              <Link
                                key={idx}
                                href={project.techLinks[idx]}
                                target="_blank"
                                className="flex items-center gap-2 text-[#95979D] transition-colors hover:text-white"
                                data-blobity
                                data-blobity-radius="24"
                              >
                                <IconComponent className="text-[20px]" />
                                <span>{project.techNames[idx]}</span>
                              </Link>
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="lg:sticky lg:top-8 lg:w-[55%]">
                      <ImageSlider
                        images={project.images || [project.image]}
                        alt={project.name}
                      />
                    </div>
                  </div>
                </Container>
              </div>
            </div>

            {/* Video Sections - For Sentinel Prime */}
            {(project.videoDemo || project.videoExplanation) && (
              <div className="project-page-container mb-32 mt-2 px-4 md:px-8">
                <Container
                  width="100%"
                  height="auto"
                  borderRadius={25}
                  color="rgba(255, 255, 255, 0.1)"
                  blur={false}
                  grain={true}
                  top="0px"
                  left="0px"
                  angle={0}
                >
                  <div className="relative z-10 px-6 pt-10 pb-6 lg:px-8 lg:pt-12 lg:pb-8">
                    <h2 className="mb-6 pb-4 text-3xl font-bold text-white md:text-4xl">
                      Project Showcase
                    </h2>
                    <div
                      className={`grid gap-4 ${project.videoDemo && project.videoExplanation
                        ? "grid-cols-1 lg:grid-cols-2"
                        : "grid-cols-1"
                        }`}
                    >
                      {/* YouTube Demo Video */}
                      {project.videoDemo && (
                        <div className="relative w-full">
                          <div className="flex flex-col">
                            <h3 className="mb-3 flex items-center gap-3 text-xl font-bold text-white">
                              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-600">
                                <svg
                                  className="h-6 w-6"
                                  fill="white"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                              </span>
                              {project.id === 2 ? "Promo Video" : "Live Demonstration"}
                            </h3>
                            <div
                              className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl"
                              style={{ paddingBottom: "56.25%" }}
                            >
                              <iframe
                                className="absolute top-0 left-0 h-full w-full"
                                src={project.videoDemo}
                                title={`${project.name} Demonstration`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Local Explanation Video */}
                      {project.videoExplanation && (
                        <div className="relative w-full">
                          <div className="flex flex-col">
                            <h3 className="mb-3 flex items-center gap-3 text-xl font-bold text-white">
                              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700">
                                <svg
                                  className="h-6 w-6"
                                  fill="white"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </span>
                              Project Explanation
                            </h3>
                            <div
                              className="relative w-full overflow-hidden rounded-xl bg-black shadow-2xl"
                              style={{ paddingBottom: "56.25%" }}
                            >
                              <video
                                className="absolute top-0 left-0 h-full w-full object-contain"
                                controls
                                preload="metadata"
                                controlsList="nodownload"
                              >
                                <source
                                  src={project.videoExplanation}
                                  type="video/mp4"
                                />
                                <track
                                  kind="captions"
                                  srcLang="en"
                                  label="English"
                                />
                                Your browser does not support the video tag.
                              </video>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Container>
              </div>
            )}

            <div className="relative z-10 px-4 pb-32 md:px-8">
              <h2 className="mb-8 text-3xl font-bold text-white md:text-4xl">
                Screenshots
              </h2>
              <ImageGallery
                images={(project.images || [project.image]).filter(
                  (img) => !img.toLowerCase().includes("mockup")
                )}
                alt={project.name}
              />
            </div>

            {/* Floating Next Project Button */}
            <motion.div
              className="fixed bottom-8 right-8 z-50"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                href={`/history/v2/projects/${nextProject.id}`}
                className="group flex items-center gap-2 rounded-full bg-white px-6 py-3 text-black shadow-lg backdrop-blur-sm transition-all duration-300 hover:bg-white/90"
                data-blobity
                data-blobity-radius="24"
              >
                <span>Next: {nextProject.name}</span>
                <RiArrowRightLine className="h-5 w-5 transform transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </ProjectDetailsLayout>
    </PageTransition>
  );
}
